import React from 'react';
import { useTranslation } from 'react-i18next';
import { prettyNumber } from '../../../utils/helpers';
import { CurrentItemQuery } from '../../../graphql/graphql';
import * as S from './styled';

type DuplicateOf = NonNullable<
  NonNullable<
    NonNullable<CurrentItemQuery['item']>['rejectReason']
  >['duplicateOf']
>;

interface AdDuplicateCardProps {
  duplicateOf: DuplicateOf;
}

export const AdDuplicateCard: React.FC<AdDuplicateCardProps> = ({
  duplicateOf
}) => {
  const { t } = useTranslation();

  const isLeased = !!duplicateOf.isLeased;

  const getPriceCurrency = () => {
    if (duplicateOf.isPaidDaily === null || !isLeased) {
      return duplicateOf.price.currency;
    }
    return duplicateOf.isPaidDaily
      ? `${duplicateOf.price.currency}/${t('cards.item_card.per_day')}`
      : `${duplicateOf.price.currency}/${t('cards.item_card.per_month')}`;
  };

  const formattedPrice = duplicateOf.price.total
    ? prettyNumber(duplicateOf.price.total)
    : null;

  const prettyArea = duplicateOf.area.value
    ? prettyNumber(
        Number.isInteger(duplicateOf.area.value)
          ? duplicateOf.area.value
          : Number(duplicateOf.area.value).toFixed(1)
      )
    : null;

  const details = [
    duplicateOf.rooms != null &&
      t('pages.current_item.duplicate_card.rooms', {
        count: duplicateOf.rooms
      }),
    prettyArea != null &&
      t('pages.current_item.duplicate_card.area', {
        area: prettyArea,
        units: duplicateOf.area.units || ''
      }),
    duplicateOf.floor != null &&
      t('pages.current_item.duplicate_card.floor', {
        floor: duplicateOf.floor
      })
  ]
    .filter(Boolean)
    .join(' • ');

  const thumbnail = duplicateOf.photos?.[0]?.thumbnail;
  const location = duplicateOf.location?.fullName;

  return (
    <S.CardContainer data-cy="ad-duplicate-card">
      <S.Title>{t('pages.current_item.duplicate_card.title')}</S.Title>

      <S.ContentWrapper>
        {thumbnail && (
          <S.ImageWrapper>
            <img src={thumbnail} alt={location || ''} loading="lazy" />
          </S.ImageWrapper>
        )}

        <S.InfoColumn>
          {formattedPrice && (
            <S.Price data-cy="ad-duplicate-card__price">
              {formattedPrice} {getPriceCurrency()}
            </S.Price>
          )}
          {details && (
            <S.Details data-cy="ad-duplicate-card__details">
              {details}
            </S.Details>
          )}
          {location && (
            <S.Location data-cy="ad-duplicate-card__location">
              {location}
            </S.Location>
          )}
          <S.AdNumber data-cy="ad-duplicate-card__ad-number">
            {t('pages.current_item.duplicate_card.ad_number', {
              id: duplicateOf.id
            })}
          </S.AdNumber>
        </S.InfoColumn>
      </S.ContentWrapper>

      <S.FooterLink
        href={duplicateOf.path}
        target="_blank"
        data-cy="ad-duplicate-card__link"
      >
        {t('pages.current_item.duplicate_card.view_link')}
      </S.FooterLink>
    </S.CardContainer>
  );
};
